import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

import { getChanges, PfcError } from "./api";
import { CAUSE_NAME, COPY, money, signedPct } from "./format";

export function ChangesPanel() {
  const navigate = useNavigate();
  const { area } = useParams({ strict: false }) as { area?: string };
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem("pfc.changes.open") === "1"; } catch { return false; }
  });
  const query = useQuery({ queryKey: ["pfc", "changes"], queryFn: getChanges, retry: 1 });
  if (query.error) {
    const error = query.error as PfcError;
    if (error.status === 404) return null;
    return null;
  }
  const data = query.data?.data;
  if (!data?.run_id) return null;
  const run = (data.previous_run_id ?? data.run_id).slice(0, 8);
  if (data.total === 0) return <Card><CardHeader className="font-bold">{COPY.changesNone(run)}</CardHeader></Card>;

  const toggle = () => setOpen((wasOpen) => {
    try { localStorage.setItem("pfc.changes.open", wasOpen ? "0" : "1"); } catch { /* Browser storage may be unavailable. */ }
    return !wasOpen;
  });
  const openHistory = () => void navigate({ to: "/app/$area/$module/$tab", params: { area: area ?? "", module: "fulfilment_costs", tab: "history" } });

  return <Card>
    <CardHeader className="p-0">
      <Button variant="ghost" className="w-full justify-between" aria-expanded={open} onClick={toggle} data-nonmutating>
        <span className="font-bold">{open ? "▾" : "▸"} {COPY.changesTitle(run, data.total)}</span>
        <span className="text-sm text-ks-grey-1">component values that moved between the last two runs</span>
      </Button>
    </CardHeader>
    {open && <CardContent>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-ks-grey-1"><th>SKU</th><th>Store</th><th>Component</th><th className="text-right">Old to new</th><th>Cause</th><th>Beyond band?</th></tr></thead>
        <tbody>{data.changes.map((change, index) => <tr key={index}>
          <td className="font-bold">{change.xero_sku}</td>
          <td>{change.marketplace_name ?? change.channel_location}{change.country !== "ALL" ? ` · ${change.country}` : ""}</td>
          <td>{change.component}</td>
          <td className="text-right">{change.old_value === null ? "—" : money(change.old_value, change.currency)} to <b>{money(change.new_value, change.currency)}</b>{change.change_pct ? <span className="ml-1 text-xs text-ks-grey-1">{signedPct(change.change_pct)}</span> : null}</td>
          <td>{CAUSE_NAME[change.cause] ?? change.cause}</td>
          <td>{change.anomaly_id !== null ? <Button variant="link" className="text-ks-danger" data-nonmutating onClick={openHistory}>{COPY.beyondBand} → Cost History</Button> : null}</td>
        </tr>)}</tbody>
      </table>
      {data.capped ? <p className="text-sm text-ks-grey-1">{COPY.changesCapped(data.total - data.changes.length)}</p> : null}
    </CardContent>}
  </Card>;
}
